/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { TranslateStage } from '../translate-stage';
import type { TranslateServerSideModel } from '../../models/translate-server-side-model';
import { TranslateStep, type TranslateOptions } from '../../types';
import { OriginalInputModel } from '@/domains/translate-bot/models/original-input-model';
import { ASYNC_THRESHOLD } from '../../constants';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { CreateSyncTranslateTaskService } from '../create-sync-translate-task-service';
import { CreateAsyncTranslateTaskService } from '../create-async-translate-task-service';
import { TranslationTaskNameGetter, TranslationTaskNameSetter } from '../task-name-manager';
import { TranslateModel } from '../../models/translate-model';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';

@injectable()
export class TranslateStageInitial implements TranslateStage {
    constructor(
        @inject(OriginalInputModel)
        private _originalInputModel: OriginalInputModel,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
        @inject(CreateSyncTranslateTaskService)
        private _createTranslateTaskService: CreateSyncTranslateTaskService,
        @inject(CreateAsyncTranslateTaskService)
        private _createAsyncTranslateTaskService: CreateAsyncTranslateTaskService,
        @inject(TranslationTaskNameGetter)
        private _taskNameGetter: TranslationTaskNameGetter,
        @inject(TranslationTaskNameSetter)
        private _taskNameSetter: TranslationTaskNameSetter,
        @inject(TranslateModel)
        private _aiTranslationModel: TranslateModel,
        @inject(RavenService)
        private _ravenService: RavenService,
    ) {}

    isPreviousStage(): boolean {
        return false;
    }

    isNextStage(): boolean {
        return false;
    }

    isCurrentStage(model: TranslateServerSideModel): boolean {
        return model.modelData.currentStep === TranslateStep.StepInitial;
    }

    async onEnter(): Promise<void> {
        const taskId = this._currentTaskService.taskId;
        this._ravenService.track(TrackEvents.TRACK_EVENT_ENTER_TRANSLATION_INITIAL, {
            eventType: TRACK_EVENT_TYPE.Translation,
            otherParam: taskId,
        });
        const title = await this._taskNameGetter.getTaskName(taskId);
        this._aiTranslationModel.update((state) => ({
            ...state,
            title,
        }));
    }

    async onLeave(): Promise<void> {
        const taskId = this._currentTaskService.taskId;
        this._ravenService.track(TrackEvents.TRACK_EVENT_LEAVE_TRANSLATION_INITIAL, {
            eventType: TRACK_EVENT_TYPE.Translation,
            otherParam: taskId,
        });
        const translateOptions = this._getTranslateOptions();
        if (this.isAsyncThreshold) {
            await this._createAsyncTranslateTask(translateOptions);
        } else {
            await this._createSyncTranslateTask(translateOptions);
        }
        await this._saveTaskName(translateOptions.text);
    }

    /**
     * 保存任务标题
     * @param content 原始文本
     */
    private _saveTaskName(content: string) {
        const taskId = this._currentTaskService.taskId;
        let title = this._aiTranslationModel.modelData.title;
        if (!title) {
            title = content.slice(0, 10);
        }
        return this._taskNameSetter.setTaskName(taskId, title);
    }


    /**
     * 是否达到异步翻译任务阈值
     */
    private get isAsyncThreshold() {
        return this._originalInputModel.modelData.input.length > ASYNC_THRESHOLD;
    }

    /**
     * 获取翻译选项
     */
    private _getTranslateOptions() {
        const { input, targetLang, sourceLang } = this._originalInputModel.modelData;
        const translateOptions: TranslateOptions = {
            text: input,
            sourceLanguage: sourceLang as string,
            targetLanguage: targetLang as string,
        };
        return translateOptions;
    }

    /**
     * 创建同步翻译任务
     */
    private _createSyncTranslateTask(translateOptions: TranslateOptions) {
        console.info('创建同步翻译任务...');
        const taskId = this._currentTaskService.taskId;
        return this._createTranslateTaskService.createTranslateTask(taskId, translateOptions);
    }

    /**
     * 创建异步翻译任务
     */
    private _createAsyncTranslateTask(translateOptions: TranslateOptions) {
        console.info('创建异步翻译任务...');
        const taskId = this._currentTaskService.taskId;
        return this._createAsyncTranslateTaskService.createAsyncTranslateTask(taskId, translateOptions);
    }
}
